import React from 'react';
import { Card, Row, Col, Button, Typography, Statistic, Timeline, Tag, Space } from 'antd';
import { 
  SafetyOutlined, 
  CheckCircleOutlined, 
  FileTextOutlined,
  DashboardOutlined,
  TeamOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  ToolOutlined,
  FireOutlined,
  RocketOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

const AboutProduct = () => {
  const navigate = useNavigate();

  const keyFeatures = [
    {
      icon: <ToolOutlined style={{ fontSize: '2.5rem', color: '#ff6b35' }} />,
      title: 'AMC Management',
      description: 'Revolutionary Annual Maintenance Contract management that transforms your service delivery. Track visits, schedule maintenance, monitor equipment lifecycle, and ensure 100% compliance with intelligent automation.',
      benefits: ['85% reduction in manual paperwork', 'Automated compliance alerts', 'Real-time contract tracking', 'Predictive maintenance scheduling']
    },
    {
      icon: <FileTextOutlined style={{ fontSize: '2.5rem', color: '#1890ff' }} />,
      title: 'Smart BOQ Generation',
      description: 'AI-powered Bill of Quantities creation with industry-specific templates, real-time pricing, and instant professional proposals. Transform your quotation process from hours to minutes.',
      benefits: ['70% faster quotation generation', 'Zero calculation errors', 'Professional proposals', 'Real-time cost optimization']  
    },
    {
      icon: <SafetyOutlined style={{ fontSize: '2.5rem', color: '#52c41a' }} />,
      title: 'Digital Fire Surveys',
      description: 'Next-generation fire safety surveys with GPS tracking, photo documentation, equipment barcode scanning, and instant compliance report generation. Field-to-office in real-time.',
      benefits: ['Mobile accessibility', 'Real-time data sync', 'Instant compliance reports', 'Equipment lifecycle tracking']
    },
    {
      icon: <DashboardOutlined style={{ fontSize: '2.5rem', color: '#722ed1' }} />,
      title: 'Intelligent Dashboard',
      description: 'Comprehensive business intelligence with predictive analytics, performance KPIs, revenue tracking, and operational insights. Make data-driven decisions every day.',
      benefits: ['360° business overview', 'Predictive maintenance alerts', 'Performance analytics', 'Revenue optimization']
    }
  ];

  const modules = [
    { name: 'Dashboard', icon: <DashboardOutlined />, description: 'Comprehensive business overview' },
    { name: 'Employee Management', icon: <TeamOutlined />, description: 'Staff scheduling & performance tracking' },
    { name: 'Customer Portal', icon: <GlobalOutlined />, description: 'Client self-service platform' },
    { name: 'AMC Management', icon: <ToolOutlined />, description: 'Contract lifecycle management' },
    { name: 'Digital Surveys', icon: <SafetyOutlined />, description: 'Mobile fire safety inspections' },
    { name: 'Smart Quotations', icon: <FileTextOutlined />, description: 'Automated proposal generation' },
    { name: 'BOQ Builder', icon: <BarChartOutlined />, description: 'Intelligent cost estimation' },
    { name: 'Purchase Orders', icon: <ShoppingCartOutlined />, description: 'Procurement management' },
    { name: 'Lead Management', icon: <RocketOutlined />, description: 'Sales pipeline optimization' },
    { name: 'Cash Ledger', icon: <DollarOutlined />, description: 'Financial tracking & reporting' },
    { name: 'License Management', icon: <SafetyOutlined />, description: 'Compliance documentation' },
    { name: 'Advanced Reports', icon: <BarChartOutlined />, description: 'Business intelligence & analytics' }
  ];

  const stats = [
    { title: '95%', subtitle: 'Reduction in Manual Errors', icon: <CheckCircleOutlined /> },
    { title: '80%', subtitle: 'Faster Report Generation', icon: <ThunderboltOutlined /> },
    { title: '60%', subtitle: 'Operational Cost Reduction', icon: <DollarOutlined /> },
    { title: '300%', subtitle: 'Average ROI in 12 Months', icon: <RocketOutlined /> }
  ];

  const timeline = [
    {
      color: '#ff6b35',
      children: (
        <div>
          <Title level={5}>Digital Transformation</Title>
          <Paragraph>Replace paper-based processes with intelligent digital workflows</Paragraph>
        </div>
      )
    },
    {
      color: '#1890ff', 
      children: (
        <div>
          <Title level={5}>Process Automation</Title>
          <Paragraph>Automate routine tasks, scheduling, and compliance monitoring</Paragraph>
        </div>
      )
    },
    {
      color: '#52c41a',
      children: (
        <div>
          <Title level={5}>Real-time Analytics</Title>
          <Paragraph>Gain instant insights with powerful reporting and dashboard analytics</Paragraph>
        </div>
      )
    },
    {
      color: '#722ed1',
      children: (
        <div>
          <Title level={5}>Business Growth</Title>
          <Paragraph>Scale operations efficiently with data-driven decision making</Paragraph>
        </div>
      )
    }
  ];

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh'
    }}>
      {/* Hero Section */}
      <section style={{ 
        padding: '4rem 2rem',
        textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.9), rgba(24, 144, 255, 0.8))',
        color: 'white'
      }}>
        <Row justify="center">
          <Col xs={24} lg={16}>
            <FireOutlined style={{ fontSize: '4rem', marginBottom: '1rem' }} />
            <Title level={1} style={{ color: 'white', fontSize: '3.5rem', marginBottom: '1rem' }}>
              Organization Name
            </Title>
            <Title level={3} style={{ color: 'white', fontWeight: 300, marginBottom: '2rem' }}>
              Powered by Inferno - The Complete Fire Safety Management Platform
            </Title>
            <Paragraph style={{ 
              fontSize: '1.2rem', 
              color: 'white', 
              marginBottom: '2rem',
              opacity: 0.95
            }}>
              Transform your fire safety operations with India's most advanced digital platform. 
              Streamline AMCs, automate surveys, generate intelligent BOQs, and ensure 100% compliance 
              with our comprehensive enterprise solution.
            </Paragraph>
            <Space size="large">
              <Button 
                type="primary" 
                size="large" 
                onClick={() => navigate('/')}
                style={{ 
                  background: '#ff6b35', 
                  borderColor: '#ff6b35',
                  height: '50px',
                  fontSize: '1rem',
                  padding: '0 2rem'
                }}
              >
                Start Your Journey
              </Button>
              <Button 
                size="large" 
                ghost
                style={{ 
                  height: '50px',
                  fontSize: '1rem',
                  padding: '0 2rem'
                }}
              >
                Watch Demo
              </Button>
            </Space>
          </Col>
        </Row>
      </section>

      {/* Stats Section */}
      <section style={{ padding: '3rem 2rem', background: 'white' }}>
        <Row gutter={[32, 32]} justify="center">
          {stats.map((stat, index) => (
            <Col xs={12} md={6} key={index}>
              <Card 
                style={{ 
                  textAlign: 'center',
                  borderRadius: '15px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
                                 styles={{ body: { padding: '2rem 1rem' } }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '1rem', color: '#1890ff' }}>
                  {stat.icon}
                </div>
                <Statistic 
                  title={stat.subtitle}
                  value={stat.title}
                  valueStyle={{ 
                    fontSize: '2.5rem', 
                    fontWeight: 'bold',
                    color: '#262626'
                  }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </section>

      {/* Key Features */}
      <section style={{ padding: '4rem 2rem', background: '#f8f9fa' }}>
        <Row justify="center" style={{ marginBottom: '3rem' }}>
          <Col xs={24} lg={16} style={{ textAlign: 'center' }}>
            <Title level={2} style={{ color: '#262626', marginBottom: '1rem' }}>
              Revolutionary Fire Safety Management
            </Title>
            <Paragraph style={{ fontSize: '1.1rem', color: '#666' }}>
              Experience the power of digital transformation with features designed specifically 
              for fire safety professionals
            </Paragraph>
          </Col>
        </Row>
        
        <Row gutter={[32, 32]} justify="center">
          {keyFeatures.map((feature, index) => (
            <Col xs={24} md={12} lg={6} key={index}>
              <Card 
                                 style={{ 
                   height: '100%',
                   borderRadius: '15px',
                   border: 'none',
                   boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                   transition: 'transform 0.3s ease'
                 }}
                 onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                 onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0px)'}
                 styles={{ body: { padding: '2rem' } }}
              >
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  {feature.icon}
                </div>
                <Title level={4} style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  {feature.title}
                </Title>
                <Paragraph style={{ color: '#666', marginBottom: '1.5rem' }}>
                  {feature.description}
                </Paragraph>
                <div>
                  {feature.benefits.map((benefit, idx) => (
                    <Tag key={idx} color="green" style={{ margin: '0.2rem' }}>
                      <CheckCircleOutlined /> {benefit}
                    </Tag>
                  ))}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </section>

      {/* Complete Module Suite */}
      <section style={{ padding: '4rem 2rem', background: 'white' }}>
        <Row justify="center" style={{ marginBottom: '3rem' }}>
          <Col xs={24} lg={16} style={{ textAlign: 'center' }}>
            <Title level={2} style={{ color: '#262626', marginBottom: '1rem' }}>
              Complete Business Suite
            </Title>
            <Paragraph style={{ fontSize: '1.1rem', color: '#666' }}>
              Everything you need to run a modern fire safety business - all in one platform
            </Paragraph>
          </Col>
        </Row>
        
        <Row gutter={[24, 24]} justify="center">
          {modules.map((module, index) => (
            <Col xs={12} sm={8} md={6} lg={4} key={index}>
              <Card 
                style={{ 
                  textAlign: 'center',
                  borderRadius: '12px',
                  height: '100%',
                  border: '1px solid #f0f0f0',
                  transition: 'all 0.3s ease'
                }}
                                 styles={{ body: { padding: '1.5rem 1rem' } }}
                 hoverable
              >
                <div style={{ 
                  fontSize: '2rem', 
                  marginBottom: '1rem',
                  color: '#1890ff'
                }}>
                  {module.icon}
                </div>
                <Title level={5} style={{ marginBottom: '0.5rem' }}>
                  {module.name}
                </Title>
                <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                  {module.description}
                </Text>
              </Card>
            </Col>
          ))}
        </Row>
      </section>

             {/* ROI & Efficiency Section */}
       <section style={{ padding: '4rem 2rem', background: 'linear-gradient(135deg, #ff6b35 0%, #f12711 100%)', color: 'white' }}>
         <Row justify="center" style={{ marginBottom: '3rem' }}>
           <Col xs={24} lg={16} style={{ textAlign: 'center' }}>
             <Title level={2} style={{ color: 'white', marginBottom: '1rem' }}>
               Proven Business Impact & Cost Reduction
             </Title>
             <Paragraph style={{ fontSize: '1.2rem', color: 'white', opacity: 0.9 }}>
               Real results from fire safety businesses using Inferno ERP
             </Paragraph>
           </Col>
         </Row>
         
         <Row gutter={[32, 32]} justify="center">
           <Col xs={24} md={12} lg={6}>
             <Card style={{ background: 'rgba(255,255,255,0.15)', border: 'none', textAlign: 'center' }}>
               <DollarOutlined style={{ fontSize: '3rem', color: '#fff', marginBottom: '1rem' }} />
               <Title level={4} style={{ color: 'white' }}>60% Cost Reduction</Title>
               <Text style={{ color: 'white' }}>
                 Eliminate paper-based processes, reduce administrative overhead, optimize resource allocation
               </Text>
             </Card>
           </Col>
           <Col xs={24} md={12} lg={6}>
             <Card style={{ background: 'rgba(255,255,255,0.15)', border: 'none', textAlign: 'center' }}>
               <ThunderboltOutlined style={{ fontSize: '3rem', color: '#fff', marginBottom: '1rem' }} />
               <Title level={4} style={{ color: 'white' }}>5x Efficiency Boost</Title>
               <Text style={{ color: 'white' }}>
                 Automate surveys, streamline AMC management, instant BOQ generation, real-time reporting
               </Text>
             </Card>
           </Col>
           <Col xs={24} md={12} lg={6}>
             <Card style={{ background: 'rgba(255,255,255,0.15)', border: 'none', textAlign: 'center' }}>
               <CheckCircleOutlined style={{ fontSize: '3rem', color: '#fff', marginBottom: '1rem' }} />
               <Title level={4} style={{ color: 'white' }}>100% Compliance</Title>
               <Text style={{ color: 'white' }}>
                 Never miss deadlines, automated alerts, comprehensive audit trails, regulatory reporting
               </Text>
             </Card>
           </Col>
           <Col xs={24} md={12} lg={6}>
             <Card style={{ background: 'rgba(255,255,255,0.15)', border: 'none', textAlign: 'center' }}>
               <RocketOutlined style={{ fontSize: '3rem', color: '#fff', marginBottom: '1rem' }} />
               <Title level={4} style={{ color: 'white' }}>Scale 10x Faster</Title>
               <Text style={{ color: 'white' }}>
                 Handle more customers, expand operations, grow revenue without proportional cost increase
               </Text>
             </Card>
           </Col>
         </Row>
       </section>

       {/* Transformation Journey */}
       <section style={{ padding: '4rem 2rem', background: '#f8f9fa' }}>
         <Row gutter={[48, 48]} align="middle">
           <Col xs={24} lg={12}>
             <Title level={2} style={{ color: '#262626', marginBottom: '2rem' }}>
               Your Digital Transformation Journey
             </Title>
             <Timeline items={timeline} />
           </Col>
           <Col xs={24} lg={12}>
             <Card 
               style={{ 
                 borderRadius: '15px',
                 boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
               }}
               styles={{ body: { padding: '2rem' } }}
             >
               <Title level={3} style={{ color: '#ff6b35', marginBottom: '1.5rem' }}>
                 Why Choose Inferno ERP?
               </Title>
               <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                 <div>
                   <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '0.5rem' }} />
                   <Text strong>Industry-Specific Design:</Text>
                   <Text> Built exclusively for fire safety professionals</Text>
                 </div>
                 <div>
                   <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '0.5rem' }} />
                   <Text strong>Regulatory Compliance:</Text>
                   <Text> Always up-to-date with latest fire safety regulations</Text>
                 </div>
                 <div>
                   <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '0.5rem' }} />
                   <Text strong>Mobile-First Approach:</Text>
                   <Text> Access everything from anywhere, anytime</Text>
                 </div>
                 <div>
                   <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '0.5rem' }} />
                   <Text strong>Proven ROI:</Text>
                   <Text> Average 300% return on investment within 12 months</Text>
                 </div>
                 <div>
                   <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '0.5rem' }} />
                   <Text strong>24/7 Support:</Text>
                   <Text> Dedicated technical support when you need it</Text>
                 </div>
               </Space>
             </Card>
           </Col>
         </Row>
       </section>

      {/* Contact Section */}
      <section style={{ 
        padding: '4rem 2rem',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <Row gutter={[48, 48]} align="middle">
          <Col xs={24} lg={12}>
            <Title level={2} style={{ color: 'white', marginBottom: '2rem' }}>
              Ready to Transform Your Business?
            </Title>
            <Paragraph style={{ fontSize: '1.1rem', color: 'white', marginBottom: '2rem' }}>
              Join hundreds of fire safety professionals who have revolutionized their operations 
              with Inferno ERP. Get started today and experience the difference.
            </Paragraph>
            <Button 
              type="primary" 
              size="large"
              onClick={() => navigate('/')}
              style={{ 
                background: '#ff6b35', 
                borderColor: '#ff6b35',
                height: '50px',
                fontSize: '1rem',
                padding: '0 2rem'
              }}
            >
              Get Started Now
            </Button>
          </Col>
          <Col xs={24} lg={12}>
                         <Card 
               style={{ borderRadius: '15px' }}
               styles={{ body: { padding: '2rem' } }}
            >
              <Title level={4} style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                Get in Touch
              </Title>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <EnvironmentOutlined style={{ fontSize: '1.2rem', color: '#1890ff', marginRight: '1rem' }} />
                  <div>
                    <Text strong>Address:</Text><br />
                    <Text>Sr. No.85, Office No.7A/204, Kalpataru Estate,<br />
                    Jawalkar Nagar, Pimple Gurav, Pune - 411061</Text>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneOutlined style={{ fontSize: '1.2rem', color: '#1890ff', marginRight: '1rem' }} />
                  <div>
                    <Text strong>Phone:</Text><br />
                    <Text>+91 9970 548 822</Text>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <MailOutlined style={{ fontSize: '1.2rem', color: '#1890ff', marginRight: '1rem' }} />
                  <div>
                    <Text strong>Email:</Text><br />
                    <Text>info@ffss.in</Text>
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </section>

      {/* Footer */}
      <footer style={{ 
        padding: '2rem',
        background: '#001529',
        color: 'white',
        textAlign: 'center'
      }}>
        <Row justify="center">
          <Col xs={24}>
            <Title level={4} style={{ color: 'white', marginBottom: '1rem' }}>
              Organization Name
            </Title>
            <Paragraph style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '1rem' }}>
              Powered by Inferno ERP | Developed by Bitsmith Technologies Pvt. Ltd.
            </Paragraph>
            <Text style={{ color: 'rgba(255,255,255,0.5)' }}>
              © 2024 All rights reserved. | Transforming Fire Safety Management Across India
            </Text>
          </Col>
        </Row>
      </footer>


    </div>
  );
};

export default AboutProduct; 